import { AnimatePresence, motion } from 'framer-motion';
import { FC } from 'react';

import { pendingEventsStore } from '../../../hooks/event/pending-events.store';
import { StringKey } from '../../../lang/index';
import { EventProcessPill } from './event-process-pill';

type PendingOperationsPillProps = {
  companyId: string;
};

// Floating queue narrator at the bottom of the events page. Counts only this client's queued
// mutations (creates + edits + deletes) for the selected company — server-pending rows from
// other users are not this client's wait, so they never raise the pill. A single operation is
// named by kind ("Editing in process ..."); several collapse into a count. Entries leave the
// store when the mutation settles or its TTL expires (useEvents), so the pill can never get
// stuck on a failed apply.
export const PendingOperationsPill: FC<PendingOperationsPillProps> = ({ companyId }) => {
  const pendingCreateCount = pendingEventsStore(
    (state) => state.pendingByCompany[companyId]?.length ?? 0,
  );
  const pendingEditCount = pendingEventsStore(
    (state) => state.pendingEditsByCompany[companyId]?.length ?? 0,
  );
  const pendingDeleteCount = pendingEventsStore(
    (state) => state.pendingDeletesByCompany[companyId]?.length ?? 0,
  );
  const operationCount = pendingCreateCount + pendingEditCount + pendingDeleteCount;

  const singleOperationKey: StringKey =
    pendingEditCount > 0
      ? 'EVENT_STATUS_EDITING'
      : pendingDeleteCount > 0
        ? 'EVENT_STATUS_DELETING'
        : 'EVENT_STATUS_CREATING';

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center">
      <AnimatePresence>
        {operationCount > 0 && (
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            initial={{ opacity: 0, y: 8 }}
          >
            {operationCount === 1 ? (
              <EventProcessPill id={singleOperationKey} />
            ) : (
              <EventProcessPill
                id="EVENTS_OPERATIONS_IN_PROCESS"
                values={{ count: operationCount }}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
